import { Component, useEffect, useMemo, useRef, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HIDDEN_BALANCE, useUiStore } from '../../store/ui';
import { ScreenBackdrop } from './ScreenBackdrop';
import { CardLarge } from './CardLarge';
import { HolographicCard } from './HolographicCard';
import { CardTransactions } from './CardTransactions';
import { CardSettingsSheet } from './CardSettingsSheet';
import { TopUpSheet } from './TopUpSheet';
import { WithdrawSheet } from './WithdrawSheet';
import { ReauthFlow } from './ReauthFlow';
import { IssueCardFlow } from './IssueCardFlow';
import { PinScreen } from './PinScreen';
import { Confetti } from './Confetti';
import { CountUp } from './CountUp';
import { ChevronRight, DiagonalArrow, SettingsIcon } from './icons';
import { glassSurface } from './glass';
import cardsHeroImg from '../../assets/INCPT_wallet_no_background.webp';
import { useMe } from '../../hooks/useMe';
import {
  useCards,
  useCardTemplates,
  useAllCardBalances,
  useCardTransactions,
} from '../../hooks/useCards';
import { usePendingRecharges } from '../../hooks/useRechargeSettlements';
import { usePendingWithdrawals } from '../../hooks/useWithdrawSettlements';
import { dbg } from '../../lib/debug';
import {
  cardDetails,
  isMastercardTemplate,
  renameCard,
  setCardPin,
  setCardStatus,
  sortCardsByBalance,
  type Card,
  type CardSecret,
} from '../../lib/cards';
import { cardTxTurnover, type Tx } from './TransactionRow';

// Same frosted-glass material as the nav bar / Receive·Send buttons.
const CARD_ACTION_GLASS = glassSurface('thin');

function ActionButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-1 flex-col items-center gap-2 transition-opacity ${
        disabled ? 'pointer-events-none opacity-40' : ''
      }`}
    >
      {/* Frosted glass — same recipe as the nav bar / Receive·Send buttons. No
          overflow:hidden (breaks backdrop-filter on Android); rounded-full clips. */}
      <div
        className="relative flex h-14 w-full items-center justify-center rounded-full transition-transform active:scale-[0.98]"
        style={CARD_ACTION_GLASS}
      >
        <span className="relative z-10 flex h-full items-center justify-center text-ink">
          {icon}
        </span>
      </div>
      <span className="text-[12px] font-medium text-ink">{label}</span>
    </button>
  );
}

function EmptyCards({ onIssue }: { onIssue: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center">
      <img
        src={cardsHeroImg}
        alt=""
        className="splash-float pointer-events-none h-[200px] w-auto max-w-[280px] select-none object-contain"
      />

      <div className="-mt-2 flex flex-col items-center gap-4 text-center">
        <h2 className="text-[18px] font-medium text-ink">{t('cards.emptyTitle')}</h2>
        <div className="flex max-w-[320px] flex-col gap-3 text-[14px] leading-[1.44] text-muted">
          <p>{t('cards.emptyDesc')}</p>
          <ul className="mx-auto space-y-1 text-left">
            <li>{t('cards.emptyBullet1')}</li>
            <li>{t('cards.emptyBullet2')}</li>
            <li>{t('cards.emptyBullet3')}</li>
          </ul>
        </div>
      </div>

      <button
        onClick={onIssue}
        className="mt-5 flex h-[52px] w-[181px] items-center justify-center rounded-full bg-ink text-[14px] font-medium text-white active:opacity-90"
      >
        {t('cards.issue')}
      </button>
    </div>
  );
}

function EyeIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

/** Rounded "$320" for the today-turnover pill — matches the HTML mockup. */
function fmtTurn(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

function TurnArrow({
  color,
  up,
  size = 12,
}: {
  color: string;
  up?: boolean;
  size?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
      aria-hidden
    >
      {up ? (
        <path d="M12 20V4m-6 6 6-6 6 6" />
      ) : (
        <path d="M12 4v16m-6-6 6 6 6-6" />
      )}
    </svg>
  );
}

function dayStartMs(daysAgo = 0): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (daysAgo) d.setDate(d.getDate() - daysAgo);
  return d.getTime();
}

function sumCardTurn(txs: Tx[], sinceMs: number): { in: number; out: number } {
  let tin = 0;
  let tout = 0;
  for (const tx of txs) {
    if (tx.status !== 'success') continue;
    if (tx.cardRefunded) continue; // merchant gave the money back
    if ((tx.sortTs ?? 0) < sinceMs) continue;
    const bucket = cardTxTurnover(tx.cardType ?? '');
    if (!bucket) continue;
    // Prefer the USD/USDT debit shown in history; skip local-only rows with no equivalent.
    const debit = Number(tx.debitAmount);
    const amt =
      Number.isFinite(debit) && debit > 0
        ? debit
        : tx.asset === 'USDT' || tx.asset === 'USD'
          ? Number(tx.tokenAmount) || 0
          : 0;
    if (amt <= 0) continue;
    if (bucket === 'topup') tin += amt;
    else tout += amt;
  }
  return { in: tin, out: tout };
}

/**
 * The cards tab at rest: hero (illustration + total + today turnover), then
 * one card per row. Tapping a row opens that card. Long lists (Karty imports
 * can bring seven or more) stay reachable without a swipe carousel.
 */
function CardsList({
  cards,
  onOpen,
  onAddCard,
}: {
  cards: Card[];
  onOpen: (id: string) => void;
  onAddCard: () => void;
}) {
  const { t } = useTranslation();
  const balanceMap = useAllCardBalances(cards);
  const balanceHidden = useUiStore((s) => s.balanceHidden);
  const pendingByCard = usePendingRecharges();
  const { txs } = useCardTransactions();
  const [turnOpen, setTurnOpen] = useState(false);

  // Same rule as the home headline: pending and frozen funds aren't spendable.
  const total = cards.reduce(
    (sum, c) =>
      c.status === 'pending' || c.status === 'frozen' ? sum : sum + (balanceMap[c.id] || 0),
    0,
  );
  const sorted = sortCardsByBalance(cards, balanceMap);

  const periods = useMemo(() => {
    const rows: { label: string; in: number; out: number }[] = [
      { label: t('cards.periodToday'), ...sumCardTurn(txs, dayStartMs(0)) },
      { label: t('cards.periodWeek'), ...sumCardTurn(txs, dayStartMs(7)) },
      { label: t('cards.periodMonth'), ...sumCardTurn(txs, dayStartMs(30)) },
      { label: t('cards.periodQuarter'), ...sumCardTurn(txs, dayStartMs(90)) },
      { label: t('cards.periodHalfYear'), ...sumCardTurn(txs, dayStartMs(180)) },
      { label: t('cards.periodYear'), ...sumCardTurn(txs, dayStartMs(365)) },
    ];
    return rows;
  }, [txs, t]);
  const today = periods[0]!;

  return (
    // nav-pad clears the fixed BottomNav so the dashed "+" tile stays reachable.
    <div className="nav-pad flex flex-col">
      <div className="flex flex-col items-center px-5 text-center">
        <div className="relative flex h-[150px] items-center justify-center">
          <img
            src={cardsHeroImg}
            alt=""
            className="pointer-events-none h-[164px] w-auto max-w-[220px] select-none object-contain"
          />
        </div>
        {balanceHidden ? (
          <span className="mt-4 text-[34px] font-bold leading-none tracking-[-1.4px] tabular-nums text-ink">
            {HIDDEN_BALANCE}
          </span>
        ) : (
          <CountUp
            value={total}
            className="mt-4 text-[34px] font-bold leading-none tracking-[-1.4px] tabular-nums text-ink"
          />
        )}
        <span className="mt-1.5 text-[13px] text-muted">{t('cards.totalBalance')}</span>

        <button
          type="button"
          onClick={() => setTurnOpen((v) => !v)}
          className="mt-3 inline-flex items-center gap-[7px] whitespace-nowrap rounded-full bg-black/[0.05] px-3 py-1.5 active:opacity-70"
        >
          <span className="text-[12px] font-semibold text-muted">{t('cards.periodToday')}</span>
          <span className="inline-flex items-center gap-[3px]">
            <TurnArrow color="#22b45e" />
            <b className="text-[12px] font-bold text-[#22b45e]">
              {balanceHidden ? '••••' : fmtTurn(today.in)}
            </b>
          </span>
          <span className="inline-flex items-center gap-[3px]">
            <TurnArrow color="#6d6d80" up />
            <b className="text-[12px] font-bold text-[#4a4a58]">
              {balanceHidden ? '••••' : fmtTurn(today.out)}
            </b>
          </span>
          <ChevronRight
            size={13}
            className={`text-muted transition-transform duration-200 ${
              turnOpen ? '-rotate-90' : 'rotate-90'
            }`}
          />
        </button>
      </div>

      {turnOpen ? (
        <div className="mx-5 mt-2.5 rounded-[22px] bg-gradient-to-b from-white/[0.93] to-white/[0.72] px-[15px] py-3 shadow-[0_10px_28px_rgba(0,0,0,0.05),0_2px_6px_rgba(0,0,0,0.028)]">
          <div className="grid grid-cols-[1fr_88px_88px] gap-1.5 border-b border-black/[0.06] pb-[7px] text-[10px] font-bold uppercase tracking-[0.4px] text-muted">
            <span>{t('cards.period')}</span>
            <span className="text-right">{t('cards.toppedUp')}</span>
            <span className="text-right">{t('cards.spent')}</span>
          </div>
          {periods.map((row, i) => (
            <div
              key={row.label}
              className={`grid grid-cols-[1fr_88px_88px] items-center gap-1.5 py-2 ${
                i < periods.length - 1 ? 'border-b border-black/[0.05]' : ''
              }`}
            >
              <span className="text-[12.5px] text-muted">{row.label}</span>
              <span className="text-right text-[13px] font-semibold text-[#22b45e]">
                {balanceHidden ? '••••' : `+${fmtTurn(row.in).slice(1)}`}
              </span>
              <span className="text-right text-[13px] font-semibold text-ink">
                {balanceHidden ? '••••' : `−${fmtTurn(row.out).slice(1)}`}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mt-3 flex flex-col gap-3.5 px-5">
        {sorted.map((c) => {
          const crediting = !!pendingByCard[c.id];
          const balance = balanceHidden
            ? HIDDEN_BALANCE
            : c.status === 'pending'
              ? t('cards.pending')
              : crediting
                ? t('cards.topUpCrediting')
                : c.id in balanceMap
                  ? `$${(balanceMap[c.id] ?? 0).toFixed(2)}`
                  : '—';
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onOpen(c.id)}
              aria-label={c.label?.trim() || c.name || `•••• ${c.last4 ?? ''}`}
              className="w-full text-left transition-transform active:scale-[0.985]"
            >
              <HolographicCard
                balance={balance}
                last4={c.last4 ?? undefined}
                label={c.label?.trim() || c.name}
                mastercard={isMastercardTemplate(c.templateId)}
                frozen={c.status === 'frozen'}
                full
              />
            </button>
          );
        })}

        <button
          type="button"
          onClick={onAddCard}
          className="flex aspect-[275/174] w-full flex-col items-center justify-center gap-2 rounded-[20px] border-2 border-dashed border-black/[0.18] bg-white/[0.42] active:bg-black/[0.03]"
        >
          <span className="flex h-[46px] w-[46px] items-center justify-center rounded-full bg-black/[0.05] text-ink">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              aria-hidden
            >
              <path d="M12 5v14m-7-7h14" />
            </svg>
          </span>
          <span className="text-[15px] font-semibold text-ink">{t('cards.addCard')}</span>
          <span className="text-[12.5px] text-muted">{t('cards.addCardHint')}</span>
        </button>
      </div>
    </div>
  );
}

function FilledCards({
  cards,
  withdrawLimits,
  onSeeAll,
  onAddCard,
  scrollToIdx,
  onScrollToIdxDone,
  onActiveChange,
  backActionRef,
}: {
  cards: Card[];
  withdrawLimits?: {
    minAmountUsd: number;
    maxAmountUsd: number;
    maxPerHour?: number;
    remainingThisHour?: number;
  } | null;
  onSeeAll?: () => void;
  onAddCard: () => void;
  scrollToIdx?: number | null;
  onScrollToIdxDone?: () => void;
  onActiveChange?: (active: boolean) => void;
  backActionRef?: { current: (() => void) | null };
}) {
  const { t } = useTranslation();
  const { me, initData, canFetch } = useMe();
  const qc = useQueryClient();
  const { templates } = useCardTemplates();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  // The cards tab is a list: nothing is open until a card is tapped.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailsShown, setDetailsShown] = useState(false);
  const activeIdx = Math.max(
    0,
    cards.findIndex((c) => c.id === selectedId),
  );
  const [cardIssuedToast, setCardIssuedToast] = useState(false);

  // TEMPORARY: log the card composition on mount. Migrated/legacy cards often
  // lack expiry/last4, and template ids differ — if the crash correlates with a
  // specific shape this is where we'll see it.
  useEffect(() => {
    const shape = cards
      .map((c) => `${c.templateId}:${c.status}:${c.last4 ? 'l' : '-'}${c.expiry ? 'e' : '-'}`)
      .join(',');
    dbg(`CARDS filled n=${cards.length} [${shape}]`.slice(0, 180));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open a specific card when requested (e.g. straight after issuance, or a
  // tap on the home-screen preview).
  useEffect(() => {
    if (scrollToIdx == null) return;
    const target = cards[scrollToIdx];
    if (target) setSelectedId(target.id);
    onScrollToIdxDone?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToIdx]);

  // A card that disappears (deleted, closed) takes its detail view with it.
  useEffect(() => {
    if (selectedId && !cards.some((c) => c.id === selectedId)) setSelectedId(null);
  }, [cards, selectedId]);

  // Detect when a pending card becomes active → show success toast + confetti
  // and immediately fetch its balance (deposit is already on the card).
  const prevStatusRef = useRef<Record<string, string>>({});
  useEffect(() => {
    const prev = prevStatusRef.current;
    for (const c of cards) {
      if (prev[c.id] === 'pending' && c.status === 'active') {
        setCardIssuedToast(true);
        window.setTimeout(() => setCardIssuedToast(false), 3000);
        void qc.invalidateQueries({ queryKey: ['card', c.id, 'balance'] });
      }
    }
    prevStatusRef.current = Object.fromEntries(cards.map((c) => [c.id, c.status]));
  }, [cards]);
  const [revealed, setRevealed] = useState<Record<string, CardSecret>>({});
  const [reauth, setReauth] = useState<
    | null
    | { type: 'reveal' }
    | { type: 'status'; alter: 'LOCK' | 'UNLOCK' | 'CLOSE' }
    | { type: 'setpin' }
  >(null);
  const [revealing, setRevealing] = useState(false); // Details reveal in flight
  const [statusBusy, setStatusBusy] = useState(false); // freeze/unfreeze in flight
  const [pinStep, setPinStep] = useState<null | 'enter' | 'confirm'>(null); // card PIN entry (two-step)
  const [pinFirst, setPinFirst] = useState(''); // first entry, confirmed against the second
  const [pinToken, setPinToken] = useState<string | undefined>(); // reauth token for set-pin
  const [toast, setToast] = useState<string | null>(null);
  const openSheet = useUiStore((s) => s.openSheet);
  const closeSheet = useUiStore((s) => s.closeSheet);

  const active = selectedId ? cards.find((c) => c.id === selectedId) : undefined;
  const activeFrozen = active?.status === 'frozen';
  const needsReauth = !!me?.pinSet || !!me?.biometricEnabled;
  const activeTemplate = templates.find((x) => x.templateId === active?.templateId);
  const canSetPin = !!activeTemplate?.allowedPinLengths;

  useEffect(() => {
    onActiveChange?.(!!active);
  }, [active, onActiveChange]);

  useEffect(() => {
    if (!backActionRef) return;
    backActionRef.current = () => {
      setSelectedId(null);
      setDetailsShown(false);
    };
    return () => {
      backActionRef.current = null;
    };
  }, [backActionRef]);

  // Hide the bottom nav while the reauth or card-PIN screen is open.
  useEffect(() => {
    if (!reauth && !pinStep) return;
    openSheet();
    return () => closeSheet();
  }, [reauth, pinStep, openSheet, closeSheet]);

  // Live balances for all non-pending cards (fetched in parallel, cached per card).
  const balanceMap = useAllCardBalances(cards, active?.id);
  const balance = active ? balanceMap[active.id] : undefined;
  const pendingByCard = usePendingRecharges();
  const withdrawingByCard = usePendingWithdrawals();
  const activePending = active ? pendingByCard[active.id] : undefined;
  const activeWithdrawing = active ? withdrawingByCard[active.id] : undefined;

  // Reveal PAN/CVV (reauth-gated); flip the card once we have the secret.
  const doReveal = async (token?: string) => {
    if (!active || !canFetch) return;
    setRevealing(true);
    dbg(`CARDS reveal start idx=${activeIdx}`);
    try {
      const r = await cardDetails(initData, active.id, token);
      if ('reauth' in r) {
        dbg('CARDS reveal reauth');
        setReauth({ type: 'reveal' });
        return;
      }
      dbg('CARDS reveal ok');
      setRevealed((p) => ({ ...p, [active.id]: r }));
      setDetailsShown(true);
    } catch (e) {
      dbg(`CARDS reveal err ${String(e instanceof Error ? e.message : e).slice(0, 80)}`);
    } finally {
      setRevealing(false);
    }
  };

  const onDetails = () => {
    if (!active) return;
    dbg(`CARDS flip idx=${activeIdx} has=${revealed[active.id] ? 1 : 0}`);
    if (revealed[active.id]) {
      setDetailsShown((v) => !v);
      return;
    }
    if (needsReauth) setReauth({ type: 'reveal' });
    else void doReveal();
  };

  const doStatus = async (alter: 'LOCK' | 'UNLOCK' | 'CLOSE', token?: string) => {
    if (!active || !canFetch) return;
    setStatusBusy(true);
    try {
      await setCardStatus(initData, active.id, alter, token);
      await qc.invalidateQueries({ queryKey: ['cards'] });
    } catch {
      /* ignore */
    } finally {
      setStatusBusy(false);
      setSettingsOpen(false); // close the sheet once the status change settled
    }
  };

  const onFreezeToggle = (freeze: boolean) => {
    const alter = freeze ? 'LOCK' : 'UNLOCK';
    if (needsReauth) setReauth({ type: 'status', alter });
    else void doStatus(alter);
  };

  // Banana has no hard-delete — CLOSE terminates at the provider and soft-hides locally.
  const onDeleteCard = () => {
    if (needsReauth) setReauth({ type: 'status', alter: 'CLOSE' });
    else void doStatus('CLOSE');
  };

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2200);
  };

  const onSetPin = () => {
    setSettingsOpen(false);
    if (needsReauth) setReauth({ type: 'setpin' });
    else {
      setPinToken(undefined);
      setPinFirst('');
      setPinStep('enter');
    }
  };

  // Step 1 → remember the PIN, ask for it again to confirm.
  const onPinEntered = (pin: string) => {
    setPinFirst(pin);
    setPinStep('confirm');
  };

  // Step 2 → must match step 1, then persist. Keep the spinner up during the call.
  const onPinConfirmed = async (pin: string) => {
    if (pin !== pinFirst) {
      setPinStep(null);
      showToast(t('cards.pinMismatch'));
      return;
    }
    if (!active || !canFetch) {
      setPinStep(null);
      return;
    }
    try {
      await setCardPin(initData, active.id, pin, pinToken);
      setPinStep(null);
      showToast(t('cards.setPinDone'));
    } catch {
      setPinStep(null);
      showToast(t('cards.setPinFailed'));
    }
  };

  const doRename = async (label: string) => {
    if (!active || !canFetch) return;
    try {
      const { card: renamed } = await renameCard(initData, active.id, label || null);
      qc.setQueriesData<{ cards: Card[] }>({ queryKey: ['cards'] }, (old) =>
        old
          ? { ...old, cards: old.cards.map((card) => (card.id === renamed.id ? renamed : card)) }
          : old,
      );
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <div className="flex flex-col gap-8 pt-2">
        {!active ? (
          <CardsList
            cards={cards}
            onOpen={(id) => {
              dbg(`CARDS open ${id}`);
              setSelectedId(id);
            }}
            onAddCard={onAddCard}
          />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="px-[30px]">
              <CardLarge
                last4={
                  active.last4 ?? revealed[active.id]?.number.replace(/\s/g, '').slice(-4) ?? '••••'
                }
                balance={
                  active.status === 'pending'
                    ? ''
                    : active.id in balanceMap
                      ? `$${(balanceMap[active.id] ?? 0).toFixed(2)}`
                      : '—'
                }
                expiry={revealed[active.id]?.expiry ?? active.expiry ?? ''}
                label={active.label?.trim() || active.name}
                number={revealed[active.id]?.number}
                cvv={revealed[active.id]?.cvv}
                mastercard={isMastercardTemplate(active.templateId)}
                frozen={activeFrozen}
                flipped={detailsShown && !!revealed[active.id]?.number}
                issuing={active.status === 'pending'}
                createdAt={active.createdAt}
                onFlip={onDetails}
                onCopyNumber={
                  detailsShown && revealed[active.id]?.number
                    ? () => {
                        const n = revealed[active.id]?.number;
                        if (!n) return;
                        navigator.clipboard?.writeText(n.replace(/\s/g, '')).catch(() => {});
                        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
                        showToast(t('common.copied'));
                      }
                    : undefined
                }
              />
            </div>
          </div>
        )}
        {active && (
          <>
            {activePending && (
              <p className="-mt-4 px-[30px] text-center text-[13px] font-medium text-[#377dff]">
                {t('cards.topUpCreditingAmount', {
                  amount:
                    activePending.amount != null
                      ? Number.isInteger(activePending.amount)
                        ? String(activePending.amount)
                        : activePending.amount.toFixed(2)
                      : '…',
                })}
              </p>
            )}
            {activeWithdrawing && !activePending && (
              <p className="-mt-4 px-[30px] text-center text-[13px] font-medium text-[#377dff]">
                {t('cards.withdrawCreditingAmount', {
                  amount:
                    activeWithdrawing.amount != null
                      ? Number.isInteger(activeWithdrawing.amount)
                        ? String(activeWithdrawing.amount)
                        : activeWithdrawing.amount.toFixed(2)
                      : '…',
                })}
              </p>
            )}
            <div className="flex items-start gap-[10px] px-[30px]">
              <ActionButton
                icon={<DiagonalArrow size={24} className="rotate-180" />}
                label={t('cards.topUp')}
                onClick={() => {
                  dbg('CARDS topup open');
                  setTopUpOpen(true);
                }}
                disabled={
                  !active || activeFrozen || active.status === 'pending' || !!activeWithdrawing
                }
              />
              <ActionButton
                icon={<DiagonalArrow size={24} />}
                label={t('cards.withdraw')}
                onClick={() => {
                  dbg('CARDS withdraw open');
                  // Fresh remainingThisHour / limits before the sheet validates.
                  void qc.invalidateQueries({ queryKey: ['cards'] });
                  setWithdrawOpen(true);
                }}
                disabled={
                  !active ||
                  activeFrozen ||
                  active.status === 'pending' ||
                  !!activeWithdrawing ||
                  (withdrawLimits?.remainingThisHour != null &&
                    withdrawLimits.remainingThisHour <= 0)
                }
              />
              <ActionButton
                icon={
                  revealing ? (
                    <span className="h-[22px] w-[22px] animate-spin rounded-full border-2 border-ink/20 border-t-ink" />
                  ) : (
                    <EyeIcon size={24} />
                  )
                }
                label={t('cards.details')}
                onClick={onDetails}
                disabled={!active || activeFrozen || revealing}
              />
              <ActionButton
                icon={<SettingsIcon size={24} />}
                label={t('cards.settings')}
                onClick={() => {
                  dbg('CARDS settings open');
                  setSettingsOpen(true);
                }}
                disabled={!active}
              />
            </div>
          </>
        )}
      </div>
      {active && (
        <div className="nav-pad mt-6 flex flex-1 flex-col gap-4 rounded-t-[40px] bg-white px-4 pt-6">
          <h3 className="text-[16px] font-medium text-ink">{t('wallet.operations')}</h3>
          <CardTransactions cardId={active.id} status={active.status} />
        </div>
      )}

      <CardSettingsSheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        cardNo={activeIdx + 1}
        frozen={activeFrozen}
        adminHold={active?.adminHold === true}
        busy={statusBusy}
        template={activeTemplate}
        templateId={active?.templateId}
        cardLabel={active?.label?.trim() || active?.name}
        onRename={doRename}
        onFreeze={() => onFreezeToggle(true)}
        onUnfreeze={() => onFreezeToggle(false)}
        onSetPin={canSetPin ? onSetPin : undefined}
        onDelete={onDeleteCard}
      />

      <TopUpSheet
        card={active ?? null}
        open={topUpOpen}
        onClose={() => setTopUpOpen(false)}
        onDone={() => setTopUpOpen(false)}
      />

      <WithdrawSheet
        card={active ?? null}
        balance={balance}
        limits={withdrawLimits}
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        onDone={() => setWithdrawOpen(false)}
      />

      {pinStep && (
        <div className="fixed inset-0 z-[60]">
          {/* key forces a fresh mount between the two steps (resets digits + spinner) */}
          <PinScreen
            key={pinStep}
            title={pinStep === 'enter' ? t('cards.enterPin') : t('cards.reenterPin')}
            onComplete={pinStep === 'enter' ? onPinEntered : onPinConfirmed}
            onClose={() => {
              setPinStep(null);
              setPinFirst('');
            }}
          />
        </div>
      )}

      {toast && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1rem)]">
          <div className="animate-toast rounded-[18px] bg-ink px-5 py-3 text-[14px] font-medium text-white shadow-[0px_16px_40px_rgba(0,0,0,0.28)]">
            {toast}
          </div>
        </div>
      )}

      {/* Card issued — confetti + success toast (same style as SendScreen) */}
      {cardIssuedToast && (
        <>
          <Confetti />
          <div className="pointer-events-none fixed inset-x-0 top-0 z-[75] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1.25rem)]">
            <div className="animate-toast flex items-center gap-3 rounded-[20px] bg-ink px-5 py-4 text-white shadow-[0px_16px_40px_rgba(0,0,0,0.28)]">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#34c759]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12.5l4.5 4.5L19 7"
                    stroke="white"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div className="flex flex-col">
                <span className="text-[15px] font-semibold leading-tight">
                  {t('cards.issuedTitle')}
                </span>
                <span className="text-[13px] leading-tight text-white/70">
                  {t('cards.issuedSubtitle')}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {reauth && canFetch && (
        <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-holo">
          <ReauthFlow
            initData={initData}
            biometricEnabled={!!me?.biometricEnabled}
            onSuccess={(token) => {
              const a = reauth;
              setReauth(null);
              if (a.type === 'reveal') void doReveal(token);
              else if (a.type === 'status') void doStatus(a.alter, token);
              else {
                setPinToken(token);
                setPinFirst('');
                setPinStep('enter');
              }
            }}
            onCancel={() => setReauth(null)}
          />
        </div>
      )}
    </>
  );
}

function CardsScreenContent({
  onSeeAll,
  activeCardId,
  openIssue,
  onOpenIssueConsumed,
}: {
  onSeeAll?: () => void;
  activeCardId?: string | null;
  openIssue?: boolean;
  onOpenIssueConsumed?: () => void;
}) {
  const { t } = useTranslation();
  const { cards, withdrawLimits, loading, error } = useCards();
  const balanceMap = useAllCardBalances(cards);
  // Same order as the home carousel: highest balance first.
  const sorted = sortCardsByBalance(cards, balanceMap);
  const [issuing, setIssuing] = useState(false);
  const [scrollToNewest, setScrollToNewest] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const backActionRef = useRef<(() => void) | null>(null);

  // TEMPORARY pinpoint breadcrumbs: the last one before a PAGEHIDE/ERROR tells us
  // exactly which step of the cards flow blanked the screen.
  useEffect(() => {
    dbg(`CARDS screen loading=${loading} err=${error} n=${cards.length}`);
  }, [loading, error, cards.length]);
  // Compute scroll index from activeCardId (balance-sorted, same as home).
  const scrollToIdxFromProp =
    activeCardId != null ? sorted.findIndex((c) => c.id === activeCardId) : null;
  const [scrollToIdx, setScrollToIdx] = useState<number | null>(null);
  const filled = sorted.length > 0;

  // When activeCardId is provided (tap from home screen), trigger scroll once cards are loaded.
  const appliedActiveRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      activeCardId &&
      scrollToIdxFromProp != null &&
      scrollToIdxFromProp >= 0 &&
      appliedActiveRef.current !== activeCardId
    ) {
      appliedActiveRef.current = activeCardId;
      setScrollToIdx(scrollToIdxFromProp);
    }
  }, [activeCardId, scrollToIdxFromProp]);

  // After issuance, scroll to the newest card (pending → end of balance sort).
  useEffect(() => {
    if (!scrollToNewest || cards.length === 0) return;
    const list = sortCardsByBalance(cards, balanceMap);
    let newestIdx = 0;
    let newestTs = 0;
    list.forEach((c, i) => {
      const ts = new Date(c.createdAt).getTime();
      if (ts >= newestTs) {
        newestTs = ts;
        newestIdx = i;
      }
    });
    setScrollToIdx(newestIdx);
    setScrollToNewest(false);
  }, [scrollToNewest, cards, balanceMap]);

  // Home "+" opens this tab with the issue sheet.
  useEffect(() => {
    if (!openIssue) return;
    setIssuing(true);
    onOpenIssueConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openIssue]);

  useEffect(() => {
    if (!filled) setCardOpen(false);
  }, [filled]);

  return (
    <div className="relative min-h-full">
      <ScreenBackdrop full inTree={false} />
      <div className="relative z-10 flex min-h-full flex-col">
        <div
          className="sticky top-0 z-30 px-4 pb-2"
          style={{ paddingTop: 'calc(var(--tg-safe-top) + 0.75rem)' }}
        >
          <div className="relative flex h-8 items-center justify-center">
            {cardOpen && (
              <button
                type="button"
                onClick={() => backActionRef.current?.()}
                className="absolute left-0 flex h-8 items-center gap-1 text-[15px] font-medium text-[#377dff] active:opacity-70"
              >
                <ChevronRight size={16} className="rotate-180" />
                {t('cards.allCards')}
              </button>
            )}
            <h1 className="text-center text-[16px] font-semibold tracking-[-0.2px] text-ink">
              {t('cards.title')}
            </h1>
          </div>
        </div>
        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-ink/15 border-t-ink" />
          </div>
        ) : filled ? (
          <FilledCards
            cards={sorted}
            withdrawLimits={withdrawLimits}
            onSeeAll={onSeeAll}
            onAddCard={() => setIssuing(true)}
            scrollToIdx={scrollToIdx}
            onScrollToIdxDone={() => setScrollToIdx(null)}
            onActiveChange={setCardOpen}
            backActionRef={backActionRef}
          />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center px-4 pb-24">
            <EmptyCards onIssue={() => setIssuing(true)} />
          </div>
        )}
      </div>

      {/* Add-card action sheet (swipe to pick) */}
      {issuing && (
        <IssueCardFlow
          onClose={() => setIssuing(false)}
          onIssued={() => {
            setIssuing(false);
            setScrollToNewest(true);
          }}
        />
      )}
    </div>
  );
}

class CardsErrorBoundary extends Component<{ children: ReactNode }, { message: string | null }> {
  override state = { message: null as string | null };

  static getDerivedStateFromError(error: unknown) {
    return { message: error instanceof Error ? error.message : String(error) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    const message = error instanceof Error ? error.message : String(error);
    dbg(`CARDS ERROR ${message.slice(0, 160)} ${info.componentStack?.slice(0, 160) ?? ''}`);
  }

  override render() {
    if (this.state.message) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface px-6 text-center">
          <p className="text-[16px] font-medium text-ink">Cards render error</p>
          <p className="break-all text-[12px] text-muted">{this.state.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export function CardsScreen(props: {
  onSeeAll?: () => void;
  activeCardId?: string | null;
  openIssue?: boolean;
  onOpenIssueConsumed?: () => void;
}) {
  return (
    <CardsErrorBoundary>
      <CardsScreenContent {...props} />
    </CardsErrorBoundary>
  );
}
